import type { ReactNode } from 'react';
import { ChevronRightIcon } from 'lucide-react';

import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DISCLOSURE_CHEVRON,
  DISCLOSURE_TRIGGER,
} from '@/shared/components/disclosure';
import { InfoCallout } from '@/shared/components/InfoCallout';

export interface IncompletenessNoticeProps {
  unmeasuredRuns: number;
  unpricedRuns: number;
  /**
   * A fix-it action for the unpriced-runs case, rendered inside this same box rather
   * than as a second block beneath it (spec 092 follow-up).
   *
   * Optional and page-supplied rather than a hardcoded link: this component is shared
   * with the workspace usage page (`UsagePage`), which has no org-admin route to send a
   * reader to. Only the organization console's `AdminOverviewPage` passes one.
   */
  action?: ReactNode;
}

/**
 * How much of the figures above is missing, and why (spec 072 FR-019).
 *
 * The shared information callout (spec 226, spec 231 FR-004): a muted surface, the
 * information icon, announced as a status because it reports on the figures beside
 * it. It used to be a dashed box, and a red one when runs were unpriced (spec 094).
 * Red is for errors ("One app, not three" review): nothing here has failed — an
 * omission is not a fault, and a reader shown red looked for something wrong where
 * nothing was. Spec 094's escalation is carried by the wording, which already says
 * which case is the money-relevant one.
 *
 * Absent entirely when both counts are zero. A permanently visible reassurance is a line
 * readers learn to skip, and this one needs to be read on the day it appears.
 *
 * The two counts are distinct and both are named. **Unmeasured** means the run's token
 * counts could not be recovered; **unpriced** means the model has no rate covering when
 * it ran. They have different causes and different fixes — one is an infrastructure
 * problem, the other is somebody not having loaded a price — and collapsing them into
 * "missing" would send a reader to the wrong place.
 */
export function IncompletenessNotice({
  unmeasuredRuns,
  unpricedRuns,
  action,
}: IncompletenessNoticeProps) {
  if (unmeasuredRuns === 0 && unpricedRuns === 0) return null;

  return (
    // The action belongs to the explanation it fixes: inside the same box, so a reader
    // who has just been told something is missing finds the remedy in the same glance.
    // It stays on the line, outside Details, so the fix is never one press away.
    <InfoCallout
      role="status"
      action={unpricedRuns > 0 && action ? action : undefined}
    >
      {/*
        One line, the sentences on request (spec 232 FR-005). The two sentences were
        the second-loudest block on the page for what is a caveat; the counts are what
        a reader needs at a glance, and the reasons are there for whoever asks.
      */}
      <Collapsible>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span>{summaryLine(unmeasuredRuns, unpricedRuns)}</span>
          <CollapsibleTrigger className={DISCLOSURE_TRIGGER}>
            <ChevronRightIcon
              aria-hidden="true"
              className={DISCLOSURE_CHEVRON}
            />
            Details
          </CollapsibleTrigger>
        </div>
        <CollapsiblePanel>
          <p className="text-muted-foreground mt-2">
            The figures above are incomplete.
            {unmeasuredRuns > 0 ? (
              <>
                {' '}
                {runs(unmeasuredRuns)} could not be measured, so nothing they
                consumed is counted anywhere on this page.
              </>
            ) : null}
            {unpricedRuns > 0 ? (
              <>
                {' '}
                {runs(unpricedRuns)} ran on a model with no price for that date,
                so their tokens are counted and their cost is not.
              </>
            ) : null}
          </p>
        </CollapsiblePanel>
      </Collapsible>
    </InfoCallout>
  );
}

/**
 * "Figures incomplete: 6 runs not measured, 2 without a price." Both counts are still
 * named apart, for the reason above; the noun is said once, on the first count.
 */
function summaryLine(unmeasured: number, unpriced: number): string {
  const parts: string[] = [];
  if (unmeasured > 0) parts.push(`${runs(unmeasured)} not measured`);
  if (unpriced > 0) {
    parts.push(
      parts.length > 0
        ? `${String(unpriced)} without a price`
        : `${runs(unpriced)} without a price`,
    );
  }
  return `Figures incomplete: ${parts.join(', ')}.`;
}

function runs(count: number): string {
  return count === 1 ? '1 run' : `${String(count)} runs`;
}
